import type * as React from 'react';
import { Notification } from '@/modules/Notification';
import type { NotificationIntent } from '@/modules/Notification';
import type { AnimationAlert } from '@/shared/types/alert';
import type { NotificationProps } from '@/shared/types/message';
import { cn } from '@/shared/utils/cn';
import {
  mergeNotificationCard,
  messageQueueEntryToNotification,
  resolveNotificationClickHandler,
} from './notificationItemCardMapping';
import { FULL_LIST_GAP_PX, STACK_EXPAND_EASE } from './notificationItemStackLayout';

/** Props internas de la lista vertical “todas las notificaciones” dentro de un mazo. */
export interface NotificationItemFullListPanelProps {
  /** Filas en orden de pintado (p. ej. reciente → antigua). */
  queue: NotificationProps[];
  /** Id de la entrada que muestra temporizador (la más reciente del servicio). */
  timerTargetId: number | undefined;
  /** Intención visual aplicada a cada fila. */
  intent: NotificationIntent;
  /** Estado de salida común del contenedor durante dismiss del mazo. */
  isLeaving: boolean;
  /** Dispara la transición de entrada (opacidad / translate) al abrir la lista. */
  listMotionEnter: boolean;
  /** Si el servicio muestra barra de tiempo en las tarjetas. */
  showTimer: boolean;
  /** Duración por defecto del servicio cuando la entrada no define `duration`. */
  notificationDuration: number;
  /**
   * Clases extra para envolver la barra (atenúación con hover en stack); recibe ms efectivos o `undefined`.
   */
  progressBarClassWhenStackHover: (durationMs: number | undefined) => string | undefined;
  /** Handler de cierre por entrada (devuelve el manejador de botón). */
  handleDismissOne: (n: NotificationProps) => (e: React.MouseEvent<HTMLButtonElement>) => void;
}

/**
 * Lista vertical completa del mazo (todas las notificaciones en columna).
 *
 * @example
 * // Uso normal vía `NotificationItem`; este fragmento ilustura la firma:
 * <NotificationItemFullListPanel
 *   queue={[...group.queue].reverse()}
 *   timerTargetId={group.queue[group.queue.length - 1]?.id}
 *   intent="critica"
 *   isLeaving={group.isLeaving}
 *   listMotionEnter={motion}
 *   showTimer
 *   notificationDuration={5000}
 *   progressBarClassWhenStackHover={() => undefined}
 *   handleDismissOne={(n) => (e) => { e.stopPropagation(); dismiss(n.id); }}
 * />
 */
export function NotificationItemFullListPanel({
  queue,
  timerTargetId,
  intent,
  isLeaving,
  listMotionEnter,
  showTimer,
  notificationDuration,
  progressBarClassWhenStackHover,
  handleDismissOne,
}: NotificationItemFullListPanelProps) {
  return (
    <div
      data-testid="notification-full-list"
      className={cn(
        'ui-flex ui-w-full ui-flex-col',
        'ui-transition-[opacity,transform]',
        STACK_EXPAND_EASE,
        listMotionEnter ? 'ui-translate-y-0 ui-opacity-100' : 'ui-translate-y-2 ui-opacity-0'
      )}
      style={{ gap: FULL_LIST_GAP_PX }}
      role="list"
      aria-label="Todas las notificaciones del grupo"
    >
      {queue.map((notification) => (
        <div key={notification.id} className="ui-w-full ui-shrink-0" role="listitem">
          <Notification
            {...mergeNotificationCard(
              messageQueueEntryToNotification(notification, intent, {
                className: 'ui-w-full',
                isLeaving,
                animation: (notification.animation ?? 'fade') as AnimationAlert,
                showActionButtons: true,
                duration:
                  showTimer && notification.id === timerTargetId
                    ? (notification.duration ?? notificationDuration)
                    : undefined,
                progressBarClassName: progressBarClassWhenStackHover(
                  showTimer && notification.id === timerTargetId
                    ? (notification.duration ?? notificationDuration)
                    : undefined
                ),
                footer: null,
                onClose: notification.showCloseButton ? handleDismissOne(notification) : undefined,
                onClick: resolveNotificationClickHandler(notification),
              }),
              notification.notificationCard
            )}
          />
        </div>
      ))}
    </div>
  );
}

NotificationItemFullListPanel.displayName = 'NotificationItemFullListPanel';
